// Click-to-open detail view for a single Capture/Clip — shows the full 240² media large
// (crisp, never blurred) beyond the grid thumbnail, with a Download action. Reuses the
// Contact-Sheet overlay shell. Closes on the ×, a click on the backdrop, or Escape.
// A Clip is a GIF, so it loops here on its own; a still shows large.

import { useEffect, useRef, useState } from "react";
import type { MediaItem } from "../api";
import { downloadItem, filenameFor } from "../download";
import { Modal } from "./Modal";

export interface MediaLightboxProps {
  item: MediaItem;
  onClose: () => void;
  /** Override the download flow (tests inject a stub; default hits the network). */
  onDownload?: (item: MediaItem) => Promise<void>;
  /** Take this Capture into the Look editor as the source/palette seed (closes the lightbox). */
  onUseInEditor?: (item: MediaItem) => void;
  /**
   * Step to the previous / next loaded item — arrow keys, the on-screen chevrons, and a
   * horizontal swipe all route here. The grid passes a handler ONLY when that neighbour is
   * already loaded, so at the loaded ends the affordance disappears — clamp, not wrap, and
   * never paginate (the grid owns that policy).
   */
  onPrev?: () => void;
  onNext?: () => void;
  /** 1-based position + count of the loaded gallery ("3 / 42") — omitted ⇒ no counter. */
  position?: number;
  total?: number;
  /** Media Archive (ADR-0058): `archivedView` flips the action Archive→Restore; it mutates then closes the lightbox. */
  archivedView?: boolean;
  onArchive?: (item: MediaItem) => void;
  onRestore?: (item: MediaItem) => void;
  mutating?: boolean;
}

export function MediaLightbox({ item, onClose, onDownload, onUseInEditor, onPrev, onNext, position, total, archivedView = false, onArchive, onRestore, mutating = false }: MediaLightboxProps): React.JSX.Element {
  const name = filenameFor(item);
  // The lightbox's own busy/failed state (mirrors MediaCard) — the grid card's feedback is
  // hidden behind this open overlay, so a silent failure here looked like success.
  const [downloading, setDownloading] = useState(false);
  const [failed, setFailed] = useState(false);
  // The Archive/Restore action (ADR-0058): Restore in the Archived view, else Archive. Mutates then
  // closes the lightbox (the item is leaving this view). Null when no handler is wired.
  const archiveAction =
    archivedView && onRestore
      ? { label: "Restore", run: onRestore }
      : !archivedView && onArchive
        ? { label: "Archive", run: onArchive }
        : null;

  // Arrow-key navigation across the loaded gallery while the lightbox is mounted (the
  // keyboard lane beside the chevrons + swipe). Left/Right fire the neighbour handler
  // when present; a missing handler means we're at a loaded end, so the key is a no-op.
  // preventDefault stops the page scrolling on arrow. Escape stays useModal's business.
  useEffect(() => {
    if (!onPrev && !onNext) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "ArrowLeft" && onPrev) {
        e.preventDefault();
        onPrev();
      } else if (e.key === "ArrowRight" && onNext) {
        e.preventDefault();
        onNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onPrev, onNext]);

  // Touch/pointer swipe: a mostly-horizontal drag ≥ 48px steps to the neighbour (left =
  // next, matching a photo-roll). Tracked on the image only, so the buttons stay taps.
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  function onPointerDown(e: React.PointerEvent): void {
    swipeStart.current = { x: e.clientX, y: e.clientY };
  }
  function onPointerUp(e: React.PointerEvent): void {
    const start = swipeStart.current;
    swipeStart.current = null;
    if (!start) return;
    if (e.pointerType === "mouse") return; // a sloppy mouse drag is not a swipe — chevrons/keys serve mice
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    if (dx < 0) onNext?.();
    else onPrev?.();
  }

  async function download(): Promise<void> {
    setDownloading(true);
    setFailed(false);
    try {
      await (onDownload ?? downloadItem)(item);
    } catch {
      setFailed(true); // e.g. an expired presigned URL on a long-open tab — offer Retry
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Modal label={name} onClose={onClose} testId="media-lightbox" className="media-lightbox">
      {/* House a11y pattern (#139): a visually-hidden hint announces the arrow-key affordance
          inside the dialog when navigation is wired (mirrors the grid's visually-hidden region). */}
      {(onPrev || onNext) && (
        <span className="visually-hidden">Use the left and right arrow keys to move between items.</span>
      )}
      {/* crossOrigin matches the grid <img> so the cached CORS response is reused (ADR-0022). */}
      <img
        className="sheet-overlay__img media-lightbox__img"
        src={item.url}
        alt={name}
        crossOrigin="anonymous"
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
      />
      {/* On-screen chevrons — the mouse/touch lane the arrow keys can't serve. Each renders
          only while its neighbour exists (clamp-not-wrap made visible). */}
      {onPrev && (
        <button
          type="button"
          className="media-lightbox__nav media-lightbox__nav--prev"
          onClick={onPrev}
          aria-label="Previous item"
          data-testid="lightbox-prev"
        >
          ‹
        </button>
      )}
      {onNext && (
        <button
          type="button"
          className="media-lightbox__nav media-lightbox__nav--next"
          onClick={onNext}
          aria-label="Next item"
          data-testid="lightbox-next"
        >
          ›
        </button>
      )}
      <div className="sheet-overlay__ctl">
        <span className="media-lightbox__name">{name}</span>
        {position != null && total != null && total > 0 && (
          <span className="media-lightbox__pos" data-testid="lightbox-position">
            {position} / {total}
          </span>
        )}
        {onUseInEditor && (
          <button
            type="button"
            className="btn"
            onClick={() => { onUseInEditor(item); onClose(); }}
            data-testid="lightbox-use-editor"
          >
            🎨 Use in editor
          </button>
        )}
        {archiveAction && (
          <button
            type="button"
            className="btn"
            data-testid="lightbox-archive"
            disabled={mutating}
            onClick={() => { archiveAction.run(item); onClose(); }}
          >
            {archiveAction.label}
          </button>
        )}
        <button
          type="button"
          className="btn btn--primary"
          onClick={download}
          disabled={downloading}
          data-testid="lightbox-download"
        >
          {downloading ? "…" : failed ? "Retry download" : "Download"}
        </button>
      </div>
    </Modal>
  );
}
